import { AD_SIZE_FIELD, FRAME_COPY_TYPES, specOf, type AssetType } from './assetTypes';
import { METADATA_FIELD_LABELS } from './metadataFields';

/**
 * UI-side taxonomy: filter presets and the
 * publish-eligibility rules.
 *
 * Deliberately NOT the controlled vocabularies. Nameplates (and the carline
 * code each implies), business units, tiers, AI indicators, agencies and
 * domains all come from `GET /api/vocabulary` via `vocabulary/useVocabulary.ts`;
 * the backend's `src/core/vocabulary.py` is their only definition. The lists
 * that remain here are the ones the backend does not own as metadata
 * vocabularies: frame rates, the platform list mirrored from the shared
 * taxonomy, and copy element types. The four kinds of asset live in
 * `./assetTypes.ts`.
 */

export interface TaxonomyOption {
  value: string;
  label: string;
}

/**
 * The "nobody knows" answers (`*_UNKNOWN` in the backend's `vocabulary.py`).
 * The options themselves come from the vocabulary like any other; these name
 * the ones with rules attached. Only a source image picks them (anything cut
 * from one holds them by inheriting), and unknown channel rights stand alone.
 */
export const UNKNOWN_VALUES = {
  business_unit: 'unknown',
  channel_rights: 'unknown',
  source_agency: 'Unknown',
} as const;

/** Master assets are English and pan-European by definition; the server sets both. */
export const OLA_MARKET = 'EU';
export const OLA_LANGUAGE = 'EN';

export const FRAME_RATE_OPTIONS: TaxonomyOption[] = [
  { value: '23.976', label: '23.976 fps' },
  { value: '24', label: '24 fps' },
  { value: '25', label: '25 fps' },
  { value: '29.97', label: '29.97 fps' },
  { value: '30', label: '30 fps' },
  { value: '50', label: '50 fps' },
  { value: '59.94', label: '59.94 fps' },
  { value: '60', label: '60 fps' },
];

/**
 * Mirrors the shared taxonomy (`packages/shared-taxonomy/…/platform_rules.json`),
 * which both backends validate against. YouTube is absent on purpose: it is a
 * format Google sells, not a platform, and listing it here offered a right the
 * API rejects.
 */
export const PLATFORM_RIGHTS_OPTIONS: TaxonomyOption[] = [
  { value: 'meta', label: 'Meta' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'snapchat', label: 'Snapchat' },
  { value: 'google', label: 'Google' },
  { value: 'microsoft', label: 'Microsoft' },
];

export const COPY_TYPE_OPTIONS: TaxonomyOption[] = [
  { value: 'headline', label: 'Headline' },
  { value: 'subheadline', label: 'Subheadline' },
  { value: 'cta', label: 'CTA' },
  { value: 'offer', label: 'Offer' },
  { value: 'disclaimer', label: 'Disclaimer' },
];

/** The copy a carousel frame may carry: every type but the CTA, the platform's button. */
export const FRAME_COPY_OPTIONS: TaxonomyOption[] = COPY_TYPE_OPTIONS.filter((option) =>
  (FRAME_COPY_TYPES as readonly string[]).includes(option.value),
);

// ---------------------------------------------------------------- Filtering Taxonomy

/**
 * What each tier is for, appended to its vocabulary label in the filter
 * drawer. UI copy, not vocabulary: the tier list itself comes from the
 * backend, and a tier with no entry here is simply shown by its label.
 */
export const TIER_FILTER_DESCRIPTIONS: Record<string, string> = {
  tier_1: 'Brand / Launch',
  tier_1_5: 'Secondary Offer',
  tier_2: 'Tactical',
};

export function tierFilterLabel(value: string, label: string): string {
  const description = TIER_FILTER_DESCRIPTIONS[value];
  return description ? `${label} - ${description}` : label;
}

export const CHANNEL_OPTIONS: TaxonomyOption[] = [
  { value: 'social', label: 'Social' },
  { value: 'search', label: 'Search' },
  { value: 'programmatic', label: 'Programmatic' },
];

/** A film length (`film_durations`) as the cut is named: 15 -> "15s". */
export function filmDurationLabel(seconds: number): string {
  return `${seconds}s`;
}

export const USAGE_RIGHTS_STATUS_OPTIONS: TaxonomyOption[] = [
  { value: 'active', label: 'Active Rights' },
  { value: 'expiring_soon', label: 'Expiring Soon' },
  { value: 'expired', label: 'Expired' },
];

/**
 * Platform to channel mapping mirroring platform_rules.json.
 */
export const PLATFORM_TO_CHANNEL: Record<string, string> = {
  meta: 'social',
  facebook: 'social',
  instagram: 'social',
  linkedin: 'social',
  tiktok: 'social',
  snapchat: 'social',
  pinterest: 'social',
  google: 'search',
  microsoft: 'search',
  display: 'programmatic',
  dv360: 'programmatic',
};

/**
 * Resolve channels covered by an asset's platform rights.
 */
export function getAssetChannels(platformRights: string[] | null | undefined): string[] {
  if (!platformRights || platformRights.length === 0) return [];
  const channels = new Set<string>();
  for (const platform of platformRights) {
    const channel = PLATFORM_TO_CHANNEL[platform.toLowerCase()];
    if (channel) {
      channels.add(channel);
    }
  }
  return Array.from(channels);
}

const EXPIRING_SOON_WINDOW_DAYS = 90;

function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/**
 * Determine usage rights status ('active', 'expiring_soon', 'expired') from the rights expiry date.
 *
 * Uses timezone-safe string comparisons (YYYY-MM-DD) matching isAssetExpired, avoiding
 * UTC-midnight date shifts for users in negative UTC offsets.
 *
 * 'expiring_soon' covers today through EXPIRING_SOON_WINDOW_DAYS days from now, inclusive.
 */
export function getAssetUsageRightsStatus(
  expiryDateStr: string | null | undefined,
  referenceDate?: Date | string,
): 'active' | 'expiring_soon' | 'expired' | null {
  if (!expiryDateStr) return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(expiryDateStr);
  // A present-but-unparseable expiry drives a rights-compliance signal, so it
  // fails closed as 'expired' rather than silently behaving like an asset
  // with no expiry at all.
  if (!match) return 'expired';
  const expiryDate = match[1];

  // The reference point as both a string (for the `today` comparison, so a
  // caller-supplied `today` string is compared verbatim rather than
  // reformatted) and a `Date` (so "N days from now" can be computed once
  // regardless of which input shape was passed in).
  let today: string;
  let referenceDateObj: Date;

  if (typeof referenceDate === 'string') {
    today = referenceDate;
    const refMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(referenceDate);
    referenceDateObj = refMatch
      ? new Date(Number(refMatch[1]), Number(refMatch[2]) - 1, Number(refMatch[3]))
      : new Date();
  } else if (referenceDate instanceof Date) {
    referenceDateObj = referenceDate;
    today = toDateStr(referenceDate);
  } else {
    referenceDateObj = new Date();
    today = toDateStr(referenceDateObj);
  }

  const expiringSoonCutoff = toDateStr(addDays(referenceDateObj, EXPIRING_SOON_WINDOW_DAYS));

  if (expiryDate < today) {
    return 'expired';
  }

  if (expiryDate <= expiringSoonCutoff) {
    return 'expiring_soon';
  }

  return 'active';
}

const ALL_OPTIONS: TaxonomyOption[][] = [
  FRAME_RATE_OPTIONS,
  PLATFORM_RIGHTS_OPTIONS,
  COPY_TYPE_OPTIONS,
];

/**
 * The stored token rendered for display, for the UI-only lists above.
 *
 * Falls back to the raw value rather than blanking it: a row written before an
 * option was renamed — or by a future backend this frontend hasn't caught up
 * with — should still show what it holds instead of appearing empty. Tokens
 * from the backend-owned vocabularies (tiers, AI indicators, …) go through
 * `vocabularyLabel` in `vocabulary/useVocabulary.ts` instead.
 */
export function taxonomyLabel(value: string | null | undefined): string {
  if (!value) return '';
  for (const options of ALL_OPTIONS) {
    const match = options.find((option) => option.value === value);
    if (match) return match.label;
  }
  return value;
}

/**
 * ISO `YYYY-MM-DD` rendered as the spec's `DD/MM/YYYY`.
 *
 * Inputs keep `type="date"` and therefore ISO values; only display strings go
 * through this. String slicing rather than `Date` so a date is never shifted
 * by the viewer's timezone.
 */
export function formatDateDDMMYYYY(iso: string | null | undefined): string {
  if (!iso) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/** The one date an asset's rights run until; every kind has it. */
export function getAssetExpiryDate(asset: { expiry_rights?: string | null }): string | null {
  return asset.expiry_rights ?? null;
}

/**
 * Checks whether an asset's rights date has lapsed in browser-local time.
 *
 * Expiry is inclusive: an asset expiring today is still valid until midnight.
 * An asset expiring yesterday is expired.
 *
 * Delegates to getAssetUsageRightsStatus so expired/expiring-soon can never disagree.
 */
export function isAssetExpired(
  asset: {
    expiry_rights?: string | null;
  },
  referenceDateStr?: string,
): boolean {
  return getAssetUsageRightsStatus(getAssetExpiryDate(asset), referenceDateStr) === 'expired';
}

/**
 * Checks whether an asset's rights expire within the next 90 days (inclusive),
 * in browser-local time. Never true for an already-expired asset.
 */
export function isAssetExpiringSoon(
  asset: {
    expiry_rights?: string | null;
  },
  referenceDateStr?: string,
): boolean {
  return getAssetUsageRightsStatus(getAssetExpiryDate(asset), referenceDateStr) === 'expiring_soon';
}

/**
 * Whether an asset can be downloaded: a published source image whose rights
 * have not lapsed.
 *
 * Mirrors `download_refusal` in `apps/asset-library/backend/src/core/download.py`,
 * which remains the authority: this only decides what the UI offers, so a
 * selection is not sent for the backend to refuse.
 */
export function isDownloadable(asset: {
  asset_type: string;
  status: string;
  expiry_rights?: string | null;
}): boolean {
  return (
    asset.asset_type === 'source_image' && asset.status === 'published' && !isAssetExpired(asset)
  );
}

// ------------------------------------------------------- Publish eligibility

/**
 * Field labels as the metadata rail names them, so a blocker points at
 * something the user can actually go and find. The form's own label table,
 * plus the one pseudo-field no form shows.
 */
const PUBLISH_FIELD_LABELS: Record<string, string> = {
  ...METADATA_FIELD_LABELS,
  [AD_SIZE_FIELD]: 'Ad size',
};

/** The subset of an asset this rule reads. Keeps it testable without a full row. */
interface PublishCandidate {
  asset_type: AssetType;
  nameplate?: string[] | null;
  carline_code?: string[] | null;
  business_unit?: string[] | null;
  tier?: string[] | null;
  ai_indicator?: string | null;
  expiry_rights?: string | null;
  copy_elements?: unknown[] | null;
  platform_rights?: unknown[] | null;
  channel_rights?: unknown[] | null;
  country_rights?: unknown[] | null;
  source_agency?: string | null;
  domain?: string | null;
  format_type?: string | null;
  format_size?: string | null;
  duration_seconds?: number | null;
  campaign_name?: string | null;
  width?: number | null;
  height?: number | null;
  source_image_ids?: string[] | null;
}

/**
 * The required-field gate for publishing, mirroring `get_missing_publish_fields`
 * in `apps/asset-library/backend/src/api/assets.py`.
 *
 * What each kind owes is `publishRequired` on its entry in `ASSET_TYPES`; the
 * only thing decided here is what "present" means. One copy for the whole
 * frontend — the card's warning badge and the Publish button read the same
 * rule, where they used to carry two copies that had drifted.
 *
 * It is still a copy of the Python, and that is a real cost. The alternative is
 * posting the selection to find out, which cannot grey out a button or warn on
 * a card before the click. The backend remains the authority — anything that
 * slips past this is skipped server-side and reported from the response — so
 * the risk of drift is a stale warning, never a bad write.
 *
 * Returns rail-facing labels rather than column names, because every caller is
 * showing this to a person.
 */
export function getMissingPublishFields(asset: PublishCandidate): string[] {
  const isPresent = (field: string): boolean => {
    // `AD_SIZE_FIELD` is the one pseudo-field: read at confirm from the banner's
    // `ad.size` meta, falling back to the poster. Both absent means it never
    // confirmed cleanly, and a master with no known size cannot be reported on.
    if (field === AD_SIZE_FIELD) return asset.width !== null && asset.height !== null;
    const value = asset[field as keyof PublishCandidate];
    // Lists are checked for emptiness because `[]` is truthy.
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  };
  // By the row's rules: a carousel master owes its format, not copy.
  return specOf(asset)
    .publishRequired.filter((field) => !isPresent(field))
    .map((field) => PUBLISH_FIELD_LABELS[field] ?? field);
}

/**
 * Whether an asset still owes a link to the source image it was cut from.
 *
 * Only kinds that are cut from source images (`canHaveSources`) owe one: a
 * source image is where lineage starts, and a film is not a re-cut of a still.
 *
 * Mirrors the same condition on the publish path in
 * `apps/asset-library/backend/src/api/assets.py`.
 */
export function needsSourceImage(asset: PublishCandidate): boolean {
  if (!specOf(asset).canHaveSources) return false;
  // At least one source, however many there are. `?? []` because this is a
  // gate rather than a display: an asset whose response omitted the field
  // should read as "still owes a link", not throw on the publish path.
  return (asset.source_image_ids ?? []).length === 0;
}

/**
 * Everything standing between an asset and publication, in one list.
 *
 * The missing fields plus the two conditions that are not fields: a missing
 * source image, and rights that have already run out. Empty means publishable.
 */
export function getPublishBlockers(asset: PublishCandidate, today?: string): string[] {
  const blockers = getMissingPublishFields(asset);
  if (needsSourceImage(asset)) blockers.push('Source image');
  if (isAssetExpired(asset, today)) blockers.push('Valid rights (expired)');
  return blockers;
}
